// src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { FileViewerProvider } from '@/contexts/FileViewerContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import ErrorBoundary from '@/components/ErrorBoundary';
import GlobalFileViewer from '@/components/ui/GlobalFileViewer';

// Public Pages
import LandingPage from '@/pages/public/LandingPage';
import LoginPage from '@/pages/public/LoginPage';
import ForgotPasswordPage from '@/pages/public/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/public/ResetPasswordPage';
import PublicQuotationPage from '@/pages/public/PublicQuotationPage';

// Protected Pages
import DashboardPage from '@/pages/dashboard/DashboardPage';
import PipelinePage from '@/pages/pipeline/PipelinePage';
import PipelineStatusesPage from '@/pages/pipeline/PipelineStatusesPage';
import ProjectsListPage from '@/pages/projects/ProjectsListPage';
import AddProjectPage from '@/pages/projects/AddProjectPage';
import EditProjectPage from '@/pages/projects/EditProjectPage';
import ProjectDetailsPage from '@/pages/projects/ProjectDetailsPage';
import ProjectAccountingPage from '@/pages/projects/ProjectAccountingPage';
import ProjectQuotationPage from '@/pages/projects/ProjectQuotationPage';
import ProjectLPOContractsPage from '@/pages/projects/ProjectLPOContractsPage';
import DailyReportsPage from '@/pages/daily-reports/DailyReportsPage';
import CompanyFilesPage from '@/pages/company-files/CompanyFilesPage';
import OverheadPage from '@/pages/overhead/OverheadPage';
import ArchivedProjectsPage from '@/pages/archived/ArchivedProjectsPage';
import UsersPage from '@/pages/users/UsersPage';
import HRDashboardPage from '@/pages/hr/HRDashboardPage';
import AttendancePage from '@/pages/hr/AttendancePage';
import OvertimePage from '@/pages/hr/OvertimePage';
import AdminOvertimePage from '@/pages/hr/AdminOvertimePage';
import ResidenceManagementPage from '@/pages/hr/ResidenceManagementPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import ProfilePage from '@/pages/profile/ProfilePage';

// Productivity Pages
import DailyFocusPage from '@/pages/productivity/DailyFocusPage';
import WeeklyViewPage from '@/pages/productivity/WeeklyViewPage';
import AnalyticsPage from '@/pages/productivity/AnalyticsPage';
import ReportsPage from '@/pages/productivity/ReportsPage';

// 🔹 الصفحات الجديدة للـ Income / Expenses
import AllIncomePage from '@/pages/projects/AllIncomePage';
import AllExpensesPage from '@/pages/projects/AllExpensesPage';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          <NotificationProvider>
            <FileViewerProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <Helmet>
                  <title>Stone Beam Projects Portal</title>
                  <meta
                    name="description"
                    content="Complete project management and HR portal for Stone Beam Demolition"
                  />
                </Helmet>

                <GlobalFileViewer />

                <Routes>
                  {/* ========================================
                      PUBLIC ROUTES
                  ======================================== */}
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/landing" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/share/quotation/:token" element={<PublicQuotationPage />} />
                  <Route
                    path="/quotation/:projectId/:quotationId"
                    element={<Navigate to="/share/quotation/invalid" replace />}
                  />

                  {/* ========================================
                      PROTECTED ROUTES
                  ======================================== */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* ✅ DASHBOARD - All Authenticated Users */}
                    <Route path="/dashboard" element={<DashboardPage />} />

                    {/* ✅ PIPELINE - Admin, Manager, Engineer, Secretary */}
                    <Route
                      path="/pipeline"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer', 'Secretary']}>
                          <PipelinePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ PIPELINE STATUSES MANAGEMENT - Admin, Manager Only */}
                    <Route
                      path="/pipeline/statuses"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager']}>
                          <PipelineStatusesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        PROJECTS MODULE
                        Access: Admin, Manager, Engineer, Secretary, Accountant (View Only)
                    ======================================== */}
                    <Route
                      path="/projects"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer', 'Secretary', 'Accountant']}>
                          <ProjectsListPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Add Project - Admin, Manager, Engineer, Secretary */}
                    <Route
                      path="/projects/add"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer', 'Secretary']}>
                          <AddProjectPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Project Details - Admin, Manager, Engineer, Secretary, Accountant */}
                    <Route
                      path="/projects/:id"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer', 'Secretary', 'Accountant']}>
                          <ProjectDetailsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Edit Project - Admin, Manager, Engineer */}
                    <Route
                      path="/projects/:id/edit"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer']}>
                          <EditProjectPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Quotation - Admin, Manager, Secretary */}
                    <Route
                      path="/projects/:id/quotation"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Secretary']}>
                          <ProjectQuotationPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ LPO/Contracts - Admin, Manager, Secretary, Accountant */}
                    <Route
                      path="/projects/:id/lpo-contracts"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Secretary', 'Accountant']}>
                          <ProjectLPOContractsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Accounting - Admin, Manager, Accountant */}
                    <Route
                      path="/projects/:id/accounting"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Accountant']}>
                          <ProjectAccountingPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* 🔹 All Income - Admin, Manager, Accountant */}
                    <Route
                      path="/financial/income"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Accountant']}>
                          <AllIncomePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* 🔹 All Expenses - Admin, Manager, Accountant */}
                    <Route
                      path="/financial/expenses"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Accountant']}>
                          <AllExpensesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        DAILY REPORTS
                        Access: All Users (Everyone can submit)
                    ======================================== */}
                    <Route path="/daily-reports" element={<DailyReportsPage />} />

                    {/* ========================================
                        PRODUCTIVITY MODULE
                        Access: All Users (Personal productivity tracking)
                    ======================================== */}
                    <Route path="/productivity/daily" element={<DailyFocusPage />} />
                    <Route path="/productivity/weekly" element={<WeeklyViewPage />} />
                    <Route path="/productivity/analytics" element={<AnalyticsPage />} />
                    <Route path="/productivity/reports" element={<ReportsPage />} />

                    {/* ========================================
                        COMPANY FILES
                        Access: Admin, Manager, Engineer, Secretary
                    ======================================== */}
                    <Route
                      path="/company-files"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Engineer', 'Secretary']}>
                          <CompanyFilesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        OVERHEAD COSTS
                        Access: Admin, Manager, Accountant (View/Approve)
                    ======================================== */}
                    <Route
                      path="/overhead"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager', 'Accountant']}>
                          <OverheadPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        ARCHIVED PROJECTS
                        Access: Admin, Manager
                    ======================================== */}
                    <Route
                      path="/archived"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager']}>
                          <ArchivedProjectsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        USERS MANAGEMENT
                        Access: Admin Only
                    ======================================== */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute roles={['Admin']}>
                          <UsersPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        HR MODULE
                    ======================================== */}

                    {/* ✅ HR Dashboard - Admin, Manager */}
                    <Route
                      path="/hr/dashboard"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager']}>
                          <HRDashboardPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Attendance - All Users (View own, Admin/Manager view all) */}
                    <Route path="/hr/attendance" element={<AttendancePage />} />

                    {/* ✅ Admin Overtime Management - Admin, Manager Only */}
                    <Route
                      path="/admin/overtime"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager']}>
                          <AdminOvertimePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ✅ Overtime - All Users */}
                    <Route path="/hr/overtime" element={<OvertimePage />} />

                    {/* ✅ Residence Management - Admin, Manager */}
                    <Route
                      path="/hr/residence"
                      element={
                        <ProtectedRoute roles={['Admin', 'Manager']}>
                          <ResidenceManagementPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ========================================
                        SETTINGS & PROFILE
                        Access: All Users
                    ======================================== */}
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                  </Route>

                  {/* ✅ FALLBACK */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </Router>
            </FileViewerProvider>
          </NotificationProvider>
        </LanguageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
